import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "../../assets/css/main.scss";
import { TEAM, DESIGNMATE } from "../../config/endPoints";
import { httpGet } from "../../utils/http";
import Generation from "./Generation/index.js";
import WorkRoom from "./WorkRoom/index.js";

function TeamGenerations() {
  const navigate = useNavigate();
  const { teamId } = useParams();
  const bottomRef = useRef(null);

  const [teamsArr, setTeamsArr] = useState([]);
  const [selectedTeamName, setSelectedTeamName] = useState("");
  const [generations, setGenerations] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState("");
  const [showWorkRoom, setShowWorkRoom] = useState(false);
  const [credits, setCredits] = useState(0);

  const getGenerationsByTeamId = async (id, page = 1, limit = 10) => {
    if (!id) {
      return;
    }
    try {
      const token = localStorage.getItem("authorization");
      const getOptions = {
        method: "GET",
        url: `${DESIGNMATE}/${id}?page=${page}&limit=${limit}`,
        headers: {
          accept: "application/json",
          authorization: token,
        },
      };
      const response = await axios(getOptions);
      const newGenerations = response?.data?.generations || [];
      setGenerations((prev) =>
        page === 1 ? newGenerations : [...prev, ...newGenerations]
      );
      setHasMore(newGenerations.length > 0);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const selectTeam = (item) => {
    navigate(`/designmate/${item._id}`);
    setSelectedTeamName(item.name);
    setPage(1);
    setGenerations([]);
    getGenerationsByTeamId(item._id);
  };

  useEffect(() => {
    const teamListing = async () => {
      let teamRes = await httpGet(`${TEAM}/getUserTeamList`);
      if (teamRes?.status === 200) {
        setTeamsArr(teamRes?.data?.data);
        // Set the selected team name if teamId is in URL
        if (teamId) {
          const selectedTeam = teamRes?.data?.data.find(
            (t) => t._id === teamId
          );
          if (selectedTeam) {
            setSelectedTeamName(selectedTeam.name);
          }
        }
      }
    };

    const getCredits = async () => {
      try {
        const token = localStorage.getItem("authorization");
        const getOptions = {
          method: "GET",
          url: `${DESIGNMATE}/credits`,
          headers: {
            accept: "application/json",
            authorization: token,
          },
        };
        const result = await axios(getOptions);
        setCredits(result.data.credits);
      } catch (error) {
        console.log("Failed to fetch credits:", error);
      }
    };

    teamListing();
    getCredits();
  }, [teamId]);

  useEffect(() => {
    if (teamId) {
      getGenerationsByTeamId(teamId, page);
    }
  }, [teamId, page]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setLoading(true);
          setPage((prev) => prev + 1);
        }
      },
      { root: null, rootMargin: "0px", threshold: 0.1 }
    );

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => {
      if (bottomRef.current) observer.unobserve(bottomRef.current);
    };
  }, [hasMore, loading]);

  // Handler for image details button click
  const handleImageDetails = (image, promptText) => {
    setSelectedImage(image);
    setSelectedPrompt(promptText || "");
    setShowWorkRoom(true);
  };

  // Handler for update prompt button click - navigate back to main page with prompt
  const handleUpdatePrompt = (promptText) => {
    // Navigate back to main designmate page and set the prompt in localStorage
    // The main page will read this and set the prompt
    localStorage.setItem("updatePromptText", promptText);
    navigate("/designmate");
  };

  const headerStyles = {
    container: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "1rem",
      backgroundColor: "#f5f5f5",
      fontSize: "1.1rem",
      //   marginBottom: "1rem",
    },
    backButton: {
      cursor: "pointer",
      padding: "0.5rem 1rem",
      backgroundColor: "#4D7CFE",
      color: "white",
      border: "none",
      borderRadius: "4px",
    },
    teamSelect: {
      padding: "0.5rem",
      borderRadius: "4px",
      border: "1px solid #ddd",
    },
  };

  // If WorkRoom is active, show it instead of the generations list
  if (showWorkRoom) {
    return (
      <div style={{ height: "100vh" }}>
        <div style={{ 
          padding: "1rem", 
          backgroundColor: "#f5f5f5", 
          borderBottom: "1px solid #ddd",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <button
            style={headerStyles.backButton}
            onClick={() => setShowWorkRoom(false)}
          >
            ← Back to Generations
          </button>
          <h3 style={{ margin: 0 }}>Edit Image</h3>
        </div>
        <WorkRoom 
          initialImageUrl={selectedImage?.url} 
          prompt={selectedPrompt} 
          credits={credits}
          setCredits={setCredits}
          teamId={teamId}
        />
      </div>
    );
  }

  return (
    <div
      className="TeamGenerations"
      style={{ position: "relative", zIndex: 1 }}
    >
      <div style={headerStyles.container}>
        <div>
          <select
            style={headerStyles.teamSelect}
            value={teamId}
            onChange={(e) => {
              const selected = teamsArr.find((t) => t._id === e.target.value);
              if (selected) selectTeam(selected);
            }}
          >
            <option value="">Select a team</option>
            {teamsArr.map((team) => (
              <option key={team._id} value={team._id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>

        <button
          style={headerStyles.backButton}
          onClick={() => navigate("/designmate")}
        >
          Back to DesignMate
        </button>
      </div>

      <div style={{ padding: "1rem" }}>
        <h2>
          {selectedTeamName
            ? `${selectedTeamName}'s Generations`
            : "Select a team to view generations"}
        </h2>

        {teamId && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {generations
              ?.filter((generation) => generation.ambaramImages?.length > 0)
              ?.map((generation, index) => (
                <Generation
                  className="col-12"
                  generation={generation}
                  onImageDetails={handleImageDetails}
                  setPrompt={handleUpdatePrompt}
                  key={index}
                />
              ))}
          </div>
        )}
        {loading && teamId && <p>Loading more generations...</p>}
        <div ref={bottomRef} style={{ height: "1px" }} />
      </div>
    </div>
  );
}

export default TeamGenerations;
